import React, { FC } from "react";
import { TUpdatePageParams } from "./Table";
import { IApiPagable } from "../../../api/IPagesResponse";
import { IPageParams } from "../../../api/IApi";
import { pageSizeOptions, writePageSize } from "./tablePageSize";

const TablePagination : FC<{
  updatePageParams: TUpdatePageParams,
  pagable?: IApiPagable,
  pageParams?: IPageParams,
  pageSizeStorageKey?: string,
  className?: string,
  disabled?: boolean,
}> = ({
  updatePageParams,
  pagable,
  pageParams,
  pageSizeStorageKey,
  className = '',
  disabled = false,
}) => {
  // --
  // -- The page links hide at a single page; the rows-per-page select does
  // -- not. Picking 200 rows can collapse the result to one page, and tying
  // -- the select to the links would take it away at exactly that moment,
  // -- leaving no way back to 10.
  // --
  const pages = pagable && pagable.totalPages > 1 ? pagable : null;
  if (!pages && !pageParams) return null;

  const handleToPage = (page: number) => () => !disabled && updatePageParams({ page });

  const renderPageLink = (page: number): React.ReactNode =>
    !pages || page < 0 || page >= pages.totalPages
      ? null
      : (
        <span
          key={`${page}of${pages.totalPages}`}
          onClick={handleToPage(page)}
          role="button"
          tabIndex={-1}
          className={`
            ${pages.number === page
              ? 'text-primary border-primary hover:border-primary hover:text-primary'
              : 'text-gray-500 hover:border-gray-300 hover:text-gray-700 border-transparent'
            }
            inline-flex items-center border-t-[2px] px-3 pt-2 font-medium
             relative top-[-1px]
          `}
        >
          {page + 1}
        </span>
      );

  const renderSeparator = () => <span className="inline-flex items-center border-t-2 border-transparent px-3 pt-2 font-medium text-gray-500">...</span>;

  const chevron = (d: string) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={d}/></svg>
  );

  const step = (to: number, label: string, icon: React.ReactNode, iconFirst: boolean) => (
    <span
      onClick={handleToPage(to)}
      role="button"
      tabIndex={-1}
      className="inline-flex gap-2 items-center border-t-2 border-transparent px-1 pt-2 font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700"
    >
      {iconFirst && icon}{label}{!iconFirst && icon}
    </span>
  );

  // --
  // -- Left-aligned and stuck there. This sits inside the table's horizontal
  // -- scroller, in a box as wide as the table, so a centred block lands
  // -- halfway across a sixty-nine column view - out of sight until you
  // -- scroll to it. `sticky left-0` holds it against the left edge of the
  // -- scrollport instead, so it stays put as the table scrolls sideways.
  // --
  // -- Kept deliberately short, and `w-fit` so the sticky box is only as wide
  // -- as its contents: every pixel here is a row the table does not show.
  // --
  return (
    <nav className={`
      mt-2 w-fit
      flex items-center gap-3 border-t-[2px] border-gray-300 px-4 sm:px-0 pb-1
      sticky left-0 bg-white
      ${className}
      ${disabled ? 'opacity-50' : ''}
    `}>
      {!!pages && (
        <>
          <div className="-mt-px flex">
            {!!!pages.first && step(pages.number - 1, 'Previous', chevron('m15 18-6-6 6-6'), true)}
          </div>

          <div className="hidden md:-mt-px md:flex">
            {pages.number >= 2 && pages.totalPages > 3 && renderPageLink(0)}
            {pages.number > 2 && pages.totalPages > 4 && renderSeparator()}
            {[...Array(3)].map((el, i) => {
              if (pages.number - 1 < 0) return renderPageLink(pages.number + i)
              if (pages.number + 1 >= pages.totalPages) return renderPageLink(pages.number + i - 2)
              return renderPageLink(pages.number + i - 1)
            })}
            {pages.number < pages.totalPages - 3 && pages.totalPages > 4 && renderSeparator()}
            {pages.number < pages.totalPages - 2 && pages.totalPages > 3 && renderPageLink(pages.totalPages - 1)}
          </div>

          <div className="-mt-px flex">
            {!!!pages.last && step(pages.number + 1, 'Next', chevron('m9 18 6-6-6-6'), false)}
          </div>
        </>
      )}

      {!!pageParams && (
        <label className="flex items-center gap-2 pt-2 text-text_pale whitespace-nowrap">
          Rows per page:
          <select
            value={pageParams.size}
            disabled={disabled}
            onChange={(e) => {
              const size = Number(e.target.value);
              writePageSize(pageSizeStorageKey, size);
              // -- Back to the first page. Page 9 of the old paging need not
              // -- exist under the new size, and landing past the end shows
              // -- an empty table.
              updatePageParams({ size, page: 0 });
            }}
            className="
              rounded border border-gray-300 bg-white py-0.5 pl-2 pr-6 text-black
              focus:outline-none focus:border-primary disabled:opacity-50
            "
          >
            {pageSizeOptions(pageParams.size).map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </label>
      )}
    </nav>
  );
}

export default TablePagination;
